// useTags — Phase 2 태그 TanStack Query 훅.
// queryKey 규칙:
//   ['tags', 'autocomplete', q]  — 자동완성
//   ['tags', 'items', id]        — 태그별 items
//   ['tags']                     — 뮤테이션 후 광역 무효화용

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tagsApi,
  type CreateTagInput,
  type TagItemsOptions,
  type UpdateTagInput,
} from '../api/tags';

// ─── queryKey 상수 ───────────────────────────────────────────

export const TAGS_KEY = ['tags'] as const;
// limit 포함 — 화면마다 limit이 달라(필터바 50·태그관리 500·자동완성 20) 키를 공유하면
// 다른 화면이 받은 캐시를 보여주고, 재조회는 엉뚱한 limit으로 실행되던 문제 (2026-10-03)
export const TAGS_AUTOCOMPLETE_KEY = (q: string, limit = 20) => ['tags', 'autocomplete', q, limit] as const;
export const TAGS_ITEMS_KEY = (id: number) => ['tags', 'items', id] as const;

// ─── 조회 훅 ─────────────────────────────────────────────────

/**
 * 태그 자동완성 훅.
 * q 빈 문자열이면 전체 빈도순 반환.
 * limit 기본 20.
 */
export function useTags(q = '', limit = 20) {
  return useQuery({
    queryKey: TAGS_AUTOCOMPLETE_KEY(q, limit),
    queryFn: () => tagsApi.autocompleteTags(q, limit),
  });
}

/**
 * 특정 태그에 연결된 items 목록 훅.
 * id=null이면 비활성.
 */
export function useTagItems(id: number | null, opts: TagItemsOptions = {}) {
  return useQuery({
    queryKey: id !== null ? TAGS_ITEMS_KEY(id) : ['tags', 'items', null],
    queryFn: () => tagsApi.getTagItems(id!, opts),
    enabled: id !== null,
  });
}

// ─── 뮤테이션 훅 ─────────────────────────────────────────────

/**
 * 태그 뮤테이션 훅 모음.
 * createTag / updateTag / deleteTag 후 tags 전체 캐시 무효화.
 */
export function useTagMutations() {
  const qc = useQueryClient();

  // 태그 자동완성 캐시 전체 무효화 헬퍼
  const invalidateTags = () => qc.invalidateQueries({ queryKey: TAGS_KEY });

  const createTag = useMutation({
    mutationFn: (body: CreateTagInput) => tagsApi.createTag(body),
    onSuccess: () => {
      invalidateTags();
    },
  });

  const updateTag = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateTagInput }) =>
      tagsApi.updateTag(id, patch),
    onSuccess: (_data, variables) => {
      invalidateTags();
      // 해당 태그의 items 캐시도 무효화 (이름 변경 시 표시 업데이트)
      qc.invalidateQueries({ queryKey: TAGS_ITEMS_KEY(variables.id) });
    },
  });

  const deleteTag = useMutation({
    mutationFn: (id: number) => tagsApi.deleteTag(id),
    onSuccess: () => {
      invalidateTags();
      // items 캐시도 무효화 — 삭제된 태그 표시 제거
      qc.invalidateQueries({ queryKey: ['items'] });
    },
  });

  return { createTag, updateTag, deleteTag };
}
